import React, { useState } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import {
  LayoutDashboard, Shield, Users, Calendar, Plane, DollarSign,
  ListTodo, ChevronLeft, ChevronRight, LogOut, Home, Briefcase,
  Settings, BarChart3, Menu, X, Megaphone, Heart, Wrench, FileText, UserCog, Sparkles, UserCheck, Mailbox, Eye, ShieldCheck, Building2
} from 'lucide-react';

const NAV_ITEMS = [
  { path: '/league-admin', label: 'Executive Overview', icon: LayoutDashboard, exact: true },
  { path: '/league-admin/prospects', label: 'Prospects', icon: UserCheck },
  { path: '/league-admin/games', label: 'Game Coordination', icon: Calendar },
  { path: '/league-admin/teams', label: 'Team Operations', icon: Shield },
  { path: '/league-admin/travel', label: 'Travel & Logistics', icon: Plane },
  { path: '/league-admin/expenses', label: 'Athlete Expenses', icon: DollarSign },
  { path: '/league-admin/money-pot', label: 'Money Pot', icon: DollarSign, exact: true },
  { path: '/league-admin/money-pot/command', label: 'Money Pot · Command', icon: ShieldCheck, exact: true },
  { path: '/league-admin/money-pot/projects', label: 'Money Pot · Projects', icon: Building2 },
  { path: '/league-admin/tasks', label: 'Task Management', icon: ListTodo },
  { path: '/league-admin/marketing', label: 'Marketing', icon: Megaphone },
  { path: '/league-admin/community', label: 'Community Relations', icon: Heart },
  { path: '/league-admin/it', label: 'IT / Platform', icon: Wrench },
  { path: '/league-admin/content', label: 'Content & Comms', icon: FileText },
  { path: '/league-admin/discussions', label: 'Discussions', icon: Megaphone },
  { path: '/league-admin/member-content', label: 'Member Content', icon: Sparkles },
  { path: '/league-admin/member-waitlist', label: 'Member Waitlist', icon: Mailbox },
  { path: '/league-admin/voting', label: 'Voting & Polls', icon: Users },
  { path: '/league-admin/notifications', label: 'Notifications', icon: LayoutDashboard },
  { path: '/league-admin/reports', label: 'Reports & Analytics', icon: BarChart3 },
  { path: '/league-admin/admin-users', label: 'Admin Users', icon: UserCog },
  { path: '/league-admin/users', label: 'Users', icon: Users },
  // Phase 8.4 — Member Preview: opens the live member experience using
  // permission-based staff access. Backend `require_pro_member` already
  // bypasses ADMIN_ROLES, so staff don't need a Pro subscription.
  { path: '/member-home', label: 'Member Preview', icon: Eye, external: true },
  // Phase 8.4.1 — Team Hub Preview: lets staff pick any team and open
  // its Member Team Hub. Permission-based; does NOT enroll staff in
  // a team or affect any team analytics.
  { path: '/league-admin/team-hub-preview', label: 'Team Hub Preview', icon: Eye },
];

export const LeagueAdminLayout = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const isActive = (path, exact) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  const handleLogout = () => { logout(); navigate('/'); };

  const sidebar = (
    <div className={`flex flex-col h-full bg-[hsl(240,10%,5%)] border-r border-border transition-all duration-200 ${collapsed ? 'w-16' : 'w-60'}`}>
      {/* Logo */}
      <div className="p-3 border-b border-border flex items-center gap-2">
        <Link to="/" className="flex items-center gap-2 min-w-0">
          <img src="/logo.png" alt="FS" className="h-8 w-8 object-contain flex-shrink-0" />
          {!collapsed && <span className="font-heading font-bold text-sm uppercase tracking-tight truncate">League Admin</span>}
        </Link>
        <button onClick={() => setCollapsed(!collapsed)} className="ml-auto text-muted-foreground hover:text-foreground hidden lg:block">
          {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
        </button>
      </div>

      {/* Nav Items */}
      <nav className="flex-1 py-2 overflow-y-auto">
        {NAV_ITEMS.map(item => {
          const active = isActive(item.path, item.exact);
          return (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => setMobileOpen(false)}
              className={`flex items-center gap-3 px-3 py-2.5 mx-1.5 my-0.5 text-sm transition-colors ${
                active
                  ? 'bg-primary/15 text-primary border-l-2 border-primary'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted/20 border-l-2 border-transparent'
              }`}
              data-testid={`la-nav-${item.label.toLowerCase().replace(/[^a-z]/g, '-')}`}
            >
              <item.icon className={`w-4 h-4 flex-shrink-0 ${active ? 'text-primary' : ''}`} />
              {!collapsed && <span className="font-heading uppercase tracking-wider text-xs truncate">{item.label}</span>}
            </Link>
          );
        })}
      </nav>

      {/* Footer */}
      <div className="border-t border-border p-3">
        <Link to="/" className={`flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground mb-2 ${collapsed ? 'justify-center' : ''}`}>
          <Home className="w-3.5 h-3.5 flex-shrink-0" />
          {!collapsed && <span className="font-mono uppercase tracking-widest">Public Site</span>}
        </Link>
        {!collapsed && (
          <div className="flex items-center gap-2 mt-2 pt-2 border-t border-border">
            <div className="w-7 h-7 bg-primary/20 flex items-center justify-center flex-shrink-0">
              <Briefcase className="w-3.5 h-3.5 text-primary" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-heading uppercase truncate">{user?.name}</p>
              <p className="text-[10px] text-muted-foreground font-mono">FRONT OFFICE</p>
            </div>
            <button onClick={handleLogout} className="text-muted-foreground hover:text-destructive" data-testid="la-logout-button">
              <LogOut className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="flex h-screen overflow-hidden" data-testid="league-admin-layout">
      {/* Desktop Sidebar */}
      <div className="hidden lg:flex">{sidebar}</div>

      {/* Mobile Sidebar Overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/60" onClick={() => setMobileOpen(false)} />
          <div className="relative w-60 h-full">{sidebar}</div>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top Bar */}
        <header className="h-12 bg-[hsl(240,10%,5%)] border-b border-border flex items-center justify-between px-4 flex-shrink-0">
          <div className="flex items-center gap-3">
            <button onClick={() => setMobileOpen(true)} className="lg:hidden text-muted-foreground">
              <Menu className="w-5 h-5" />
            </button>
            <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Federation Sports · League Operations</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="font-mono text-[10px] text-muted-foreground hidden sm:block">{user?.name}</span>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto bg-background p-4 md:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
};
